<script setup>
    // Vue
    import { defineExpose, defineEmits, inject, ref, reactive, computed, watch, getCurrentInstance } from 'vue';

    // Compoent
    import PasswordInput from '@/components/PasswordInputComponent.vue';
    import Tooltip from '@/components/TooltipComponent.vue';

    // Model
    import { UserDetailRequest } from '@/models/management/managementModel';

    // Store
    import { useManagementListStore } from '@/stores/management/managementListStore';
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utils
    import Utils from '@/commonProject/helpers/utils.js';

    // Else
    import i18n from '@/common/i18n';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 利用者一覧ストア
    const managementListStore = useManagementListStore();

    // Commonストア
    const commonStore = useCommonStore();

    // 利用者詳細リクエスト項目
    const userDetailRequest = ref({});

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // 組織セレクトボックス内容リスト
    let organizationIssue = reactive([]);

    // 組織:選択した組織情報
    //const selectOrganization = ref();

    // 組織:「選択してください」表示フラグ
    //const selectFlg = ref(false);

    // 組織:第2階層PK
    //const layer2Pk = ref('');

    // 組織:第3階層PK
    //const layer3Pk = ref('');

    // ユーザーキー番号の表示非表示を制御する。trueの場合は画面に表示する。falseの場合は画面に表示しない。
    const userKeyNoSupport = ref(false);

    // ユーザーキー番号名称
    const userKeyNoNm = ref('');

    // 付加情報:性別
    const genderInfo = [
        { name: i18n.global.t('ManagementSelect.parts.dl1'), value: '1' },
        { name: i18n.global.t('ManagementSelect.parts.dl2'), value: '2' },
        { name: i18n.global.t('ManagementSelect.parts.dl3'), value: '3' },
    ];
    // 付加情報:使用言語
    const languageInfo = [
        { name: i18n.global.t('ManagementSelect.parts.dl7'), value: '1' },
        { name: i18n.global.t('ManagementSelect.parts.dl8'), value: '2' },
    ];
    // 付加情報:在職ステータス
    const incumbentStatusInfo = [
        { name: i18n.global.t('ManagementSelect.parts.dl9'), value: '1' },
        { name: i18n.global.t('ManagementSelect.parts.dl10'), value: '2' },
        { name: i18n.global.t('ManagementSelect.parts.dl11'), value: '3' },
    ];
    // 日付情報
    const selectedDate1 = ref('');
    const selectedDate2 = ref('');
    const selectedDate3 = ref('');
    const selectedDate4 = ref('');
    const selectedDate5 = ref('');
    // パスワード種別
    const pwdType = ref('');

    // 遷移元判別フラグ
    const userSelectFlg = ref(false);

    // 入力欄活性コントロール
    // ユーザーID
    const disabledUserPk = computed(() => {
        return !Utils.isNullEmpty(userDetailRequest.value.UserPk);
    });
    // 基本情報(利用開始～終了日以外)
    const disabledBasicInfo = computed(() => {
        return userDetailRequest.value.RegistMethod === '2' || userDetailRequest.value.RegistMethod === '4' || userDetailRequest.value.TouDataFlag;
    });
    // 利用開始～終了日
    const disabledUseStartEndDate = computed(() => {
        return userDetailRequest.value.RegistMethod === '2' || userDetailRequest.value.RegistMethod === '4';
    });
    // 契約
    const disabledContract = computed(() => {
        return userDetailRequest.value.RegistMethod === '2' || userDetailRequest.value.RegistMethod === '4';
    });
    // 付加情報
    const disabledAdditionalInfo = computed(() => {
        return userDetailRequest.value.RegistMethod === '2' || userDetailRequest.value.RegistMethod === '4' || userDetailRequest.value.TouDataFlag;
    });
    // パスワード種別ラジオボタン1
    const disabledIniPwdType1 = computed(() => {
        let flg = false;
        // 編集モードで本モーダルを開いた場合、一度でもログイン済みの場合は、初期パスワードの設定モードの変更は不可能。
        if (!Utils.isNullEmpty(userDetailRequest.value.UserPk) && !Utils.isNullEmpty(userDetailRequest.value.LastLoginDt)) {
            flg = true;
        }
        if (userDetailRequest.value.RegistMethod === '2' || userDetailRequest.value.RegistMethod === '4' || userDetailRequest.value.TouDataFlag) {
            flg = true;
        }
        // 管理者ユーザーを引き継いで利用者ユーザーを登録する場合は、初期パスワードの設定モードの変更は可能にする。
        if (userSelectFlg.value) {
            flg = false;
        }
        // 認証方法が「タフネス連携」のユーザーは、初期パスワードの選択肢のラジオボタンを非活性にする。
        if (userDetailRequest.value.AuthenticateMethod === '2') {
            flg = true;
        }
        return flg;
    });
    // パスワード種別ラジオボタン2
    const disabledIniPwdType2 = computed(() => {
        let flg = false;
        // 新規登録モードで本モーダルを開いた場合、一度でもログイン済みの場合は、初期パスワードの設定モードの変更は不可能。
        if (Utils.isNullEmpty(userDetailRequest.value.UserPk) || !Utils.isNullEmpty(userDetailRequest.value.LastLoginDt)) {
            flg = true;
        }
        if (userDetailRequest.value.RegistMethod === '2' || userDetailRequest.value.RegistMethod === '4' || userDetailRequest.value.TouDataFlag) {
            flg = true;
        }
        // 管理者ユーザーを引き継いで利用者ユーザーを登録する場合は、初期パスワードの設定モードの変更は可能にする。
        if (userSelectFlg.value) {
            flg = false;
        }
        // 認証方法が「タフネス連携」のユーザーは、初期パスワードの選択肢のラジオボタンを非活性にする。
        if (userDetailRequest.value.AuthenticateMethod === '2') {
            flg = true;
        }

        return flg;
    });
    // パスワード入力欄
    const disabledIniPwdFree = computed(() => {
        let flg = false;
        if (pwdType.value !== '1') {
            flg = true;
        }
        if (!Utils.isNullEmpty(userDetailRequest.value.MailAddress)) {
            flg = true;
        }
        if (userDetailRequest.value.RegistMethod === '2' || userDetailRequest.value.RegistMethod === '4' || userDetailRequest.value.TouDataFlag) {
            flg = true;
        }
        // 認証方法が「タフネス連携」のユーザーは、自由入力の初期パスワードのパスワード入力欄を非活性にする。
        if (userDetailRequest.value.AuthenticateMethod === '2') {
            flg = true;
        }
        return flg;
    });

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 初期処理 親画面から利用可能関数
     */
    async function setInfo(UserPk) {
        // 初期化する。
        await reset();

        // 本画面の描画を開始する。
        await getInfoFunction(UserPk);
    }

    /**
     * 初期化する。
     */
    async function reset() {
        // 利用者詳細リクエスト項目の初期化
        userDetailRequest.value = Object.assign({}, new UserDetailRequest());
        selectedDate1.value = '';
        selectedDate2.value = '';
        selectedDate3.value = '';
        selectedDate4.value = '';
        selectedDate5.value = '';
        pwdType.value = '1';

        // ユーザー引き継ぎ時の初期値を設定する。
        userSelectFlg.value = false;

        // 利用者詳細情報のStore情報を初期化する。
        managementListStore.userDetailInfo = {};

        // 企業契約情報のStore情報を初期化する。
        managementListStore.contractList = [];

        // 本モーダルを表示した際に、パスワード入力欄HTML要素の初期化処理を実行する。
        passwordInputComponent.value.resetInput();
    }

    /**
     * 本画面の描画を開始する。
     * @param UserPk
     */
    async function getInfoFunction(UserPk) {
        if (UserPk) {
            userDetailRequest.value.UserPk = UserPk;
            // 利用者詳細取得
            await getDetail(userDetailRequest.value.UserPk);
            // 日付フォーマット
            await setData();
            // パスワード種別調整
            if (userDetailRequest.value.IniPwdType === '2') {
                pwdType.value = '1';
            } else if (!Utils.isNullEmpty(userDetailRequest.value.LastLoginDt)) {
                pwdType.value = '0';
            } else {
                pwdType.value = userDetailRequest.value.IniPwdType;
            }

            // 認証方法が「タフネス連携」のユーザーは、初期パスワードの変更は不可なので、初期パスワードラジオボタンの座標を「設定(変更)しない」に設定する。
            if (userDetailRequest.value.AuthenticateMethod === '2') {
                // ラジオボタンの座標を「設定(変更)しない」に設定する。
                pwdType.value = '0';
            }
        }

        // - ここから - APIから取得した結果がnullの場合は、空に変換する。

        // 基本情報
        // 氏名(カナ)姓
        userDetailRequest.value.NameKnFs = Utils.changeNullValueToEmpty(userDetailRequest.value.NameKnFs);
        // 氏名(カナ)名
        userDetailRequest.value.NameKnLs = Utils.changeNullValueToEmpty(userDetailRequest.value.NameKnLs);
        // 初期パスワードの任意文字列
        userDetailRequest.value.IniPwdFree = Utils.changeNullValueToEmpty(userDetailRequest.value.IniPwdFree);
        // 利用開始日
        userDetailRequest.value.UseStartDate = Utils.changeNullValueToEmpty(userDetailRequest.value.UseStartDate);
        // 利用終了日
        userDetailRequest.value.UseEndDate = Utils.changeNullValueToEmpty(userDetailRequest.value.UseEndDate);

        // 契約割り当て
        // 契約プランのチェックボックスの選択済み一覧 一覧が存在しない場合は空配列にする。
        userDetailRequest.value.CmpContPks = Utils.isNullEmptyArray(userDetailRequest.value.CmpContPks) ? [] : userDetailRequest.value.CmpContPks;

        // 付加価値情報
        // 生年月日
        userDetailRequest.value.Birthday = Utils.changeNullValueToEmpty(userDetailRequest.value.Birthday);
        // 性別
        userDetailRequest.value.Gender = Utils.changeNullValueToEmpty(userDetailRequest.value.Gender);
        // 使用言語の選択肢
        userDetailRequest.value.Language = Utils.changeNullValueToEmpty(userDetailRequest.value.Language);
        // 入社年月日
        userDetailRequest.value.JoinedDate = Utils.changeNullValueToEmpty(userDetailRequest.value.JoinedDate);
        // 退職年月日
        userDetailRequest.value.LeavedDate = Utils.changeNullValueToEmpty(userDetailRequest.value.LeavedDate);
        // 在籍ステータスの選択肢
        userDetailRequest.value.IncumbentStatus = Utils.changeNullValueToEmpty(userDetailRequest.value.IncumbentStatus);
        // 組織マスタの選択肢
        userDetailRequest.value.OrganizationPk = Utils.changeNullValueToEmpty(userDetailRequest.value.OrganizationPk);
        // 職種マスタの選択肢
        userDetailRequest.value.JobCategoryPk = Utils.changeNullValueToEmpty(userDetailRequest.value.JobCategoryPk);
        // 職位マスタの選択肢
        userDetailRequest.value.PositionPk = Utils.changeNullValueToEmpty(userDetailRequest.value.PositionPk);
        // 雇用形態マスタの選択肢
        userDetailRequest.value.EmploymentTypePk = Utils.changeNullValueToEmpty(userDetailRequest.value.EmploymentTypePk);
        // 勤務地マスタの選択肢
        userDetailRequest.value.LocationPk = Utils.changeNullValueToEmpty(userDetailRequest.value.LocationPk);
        // 時間外労働時間マスタの選択肢
        userDetailRequest.value.OvertimePk = Utils.changeNullValueToEmpty(userDetailRequest.value.OvertimePk);
        // ユーザーキー番号
        userDetailRequest.value.UserKeyNo = Utils.changeNullValueToEmpty(userDetailRequest.value.UserKeyNo);

        // - ここまで - APIから取得した結果がnullの場合は、空に変換する。

        if (Utils.isNullEmpty(userDetailRequest.value.OrganizationPk)) {
            //selectFlg.value = true;

            // 新規登録モードの場合は、親組織のプルダウンを第1階層のみに設定する。

            // 初期化する。
            managementListStore.organizationIssue = [];
            organizationIssue.splice(0);

            // 初期選択用のプルダウン選択肢を生成する。
            const option = {
                pk: '',
                name: i18n.global.t('ManagementOrganizationDetail.parts.dl7'),
            };
            organizationIssue.push(option);

            // 選択してくださいを初期値に設定する。
            userDetailRequest.value.OrganizationPk = '';

            // 組織選択APIのリクエストボディを生成する。
            // 条件は第1階層のみの選択肢を取得したいので、何もパラメーターを設定しない。
            const queryParam = {};

            // 組織選択APIをコールする。
            const response = await managementListStore.getOrgSelAction(API_URL, queryParam);
            if (response.success) {
                // プルダウン選択肢を生成できるデータが取得できたか判定する。
                if (!Utils.isNullEmptyArray(managementListStore.organizationIssue)) {
                    // 第1階層の取得結果分だけループする。
                    for (const org of managementListStore.organizationIssue) {
                        // 第1階層に属する組織の選択肢を生成する。
                        const option = {
                            pk: org.pk,
                            name: org.name,
                        };
                        // プルダウン選択肢に格納する。
                        organizationIssue.push(option);
                    }
                }
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            // 組織保存済み利用者組織セレクトボックス内容取得
            //await getSeveOrganizationIssue();
            // 組織セレクトボックス内容取得
            await getOrganizationIssue();
        }

        // 企業契約情報取得
        await getCintractList();

        // 企業情報の詳細情報を初期化する。
        commonStore.companyInfo = {};

        // 企業情報取得APIをコールする。
        const response1 = await commonStore.getCompanyAction(API_URL);

        if (response1.success) {
            // ユーザーキー番号の表示・非表示
            userKeyNoSupport.value = commonStore.companyInfo.UserKeyNoSupport;

            // ユーザーキー番号名称
            userKeyNoNm.value = commonStore.companyInfo.UserKeyNoNm;
        } else {
            messageDialog('error', response1.errorMessage);
            if (response1.redirect) {
                location.href = response1.redirectUrl;
            }
        }

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalUserListUserDetail .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }

    defineExpose({
        setInfo,
        selectModal,
    });

    // パスワードHTML要素コンポーネントを宣言する。
    const passwordInputComponent = ref(null);

    /**
     * 日付設定処理
     */
    async function setData() {
        selectedDate1.value = convertDate2(userDetailRequest.value.UseStartDate);
        selectedDate2.value = convertDate2(userDetailRequest.value.UseEndDate);
        selectedDate3.value = convertDate2(userDetailRequest.value.Birthday);
        selectedDate4.value = convertDate2(userDetailRequest.value.JoinedDate);
        selectedDate5.value = convertDate2(userDetailRequest.value.LeavedDate);
    }

    /**
     * 日付設定処理
     */
    // ----------------------------
    // watch
    // ----------------------------
    watch(selectedDate1, (newValue) => {
        if (newValue) {
            userDetailRequest.value.UseStartDate = convertDate(newValue);
        } else {
            userDetailRequest.value.UseStartDate = '';
        }
    });
    watch(selectedDate2, (newValue) => {
        if (newValue) {
            userDetailRequest.value.UseEndDate = convertDate(newValue);
        } else {
            userDetailRequest.value.UseEndDate = '';
        }
    });
    watch(selectedDate3, (newValue) => {
        if (newValue) {
            userDetailRequest.value.Birthday = convertDate(newValue);
        } else {
            userDetailRequest.value.Birthday = '';
        }
    });
    watch(selectedDate4, (newValue) => {
        if (newValue) {
            userDetailRequest.value.JoinedDate = convertDate(newValue);
        } else {
            userDetailRequest.value.JoinedDate = '';
        }
    });
    watch(selectedDate5, (newValue) => {
        if (newValue) {
            userDetailRequest.value.LeavedDate = convertDate(newValue);
        } else {
            userDetailRequest.value.LeavedDate = '';
        }
    });

    // 日付アレンジ用変換メソッド
    function convertDate(input) {
        if (Utils.isNullEmpty(input)) {
            return '';
        }
        const [year, month, day] = input.split('-');
        return `${year}/${month}/${day}`;
    }
    function convertDate2(input) {
        if (Utils.isNullEmpty(input)) {
            return '';
        }
        const [year, month, day] = input.split('/');
        return `${year}-${month}-${day}`;
    }
    /**
     * 利用者取得処理
     */
    async function getDetail(UserPk) {
        // 利用者取得APIのクエリパラメーターを生成する。
        const queryParam = {
            UserPk: UserPk,
        };
        // 利用者取得APIをコールする。
        const response = await managementListStore.getCliDtlAction(API_URL, queryParam);
        if (response.success) {
            userDetailRequest.value = Object.assign(userDetailRequest.value, managementListStore.userDetailInfo);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 利用者登録or更新処理
     */
    async function createDetail() {
        // 初期パスワード種別の調整
        if (pwdType.value === '0') {
            // 初期パスワードを設定しない場合。
            userDetailRequest.value.IniPwdType = '0';
        } else if (pwdType.value === '1' && !Utils.isNullEmpty(userDetailRequest.value.MailAddress)) {
            // 「初期パスワードを設定する」で、メールドレスが入力されている場合。
            // ランダムのパスワードを発行する。
            userDetailRequest.value.IniPwdType = '1';
            userDetailRequest.value.IniPwdFree = null;
        } else if (pwdType.value === '1' && Utils.isNullEmpty(userDetailRequest.value.MailAddress)) {
            // 「初期パスワードを設定する」で、メールドレスが入力されていない場合。
            // 任意の文字列をパスワードとして設定する。
            userDetailRequest.value.IniPwdType = '2';
        }
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
    
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // 本モーダルの登録モードに応じて実行するAPIや、設定するリクエストボディ構造を制御する。
        if (userSelectFlg.value) {
            // 管理者から新規登録する場合。
            const requestBody = {
                UserPk: userDetailRequest.value.UserPk, // 引き継いだ管理者のPK情報を付与する。
                UserId: userDetailRequest.value.UserId,
                EmployeeNo: userDetailRequest.value.EmployeeNo,
                MailAddress: userDetailRequest.value.MailAddress,
                IniPwdType: userDetailRequest.value.IniPwdType,
                IniPwdFree: userDetailRequest.value.IniPwdFree,
                NameKjLs: userDetailRequest.value.NameKjLs,
                NameKjFs: userDetailRequest.value.NameKjFs,
                NameKnLs: Utils.changeNullValueToEmpty(userDetailRequest.value.NameKnLs),
                NameKnFs: Utils.changeNullValueToEmpty(userDetailRequest.value.NameKnFs),
                SuspendFlag: userDetailRequest.value.SuspendFlag,
                UseStartDate: Utils.changeNullValueToEmpty(userDetailRequest.value.UseStartDate),
                UseEndDate: Utils.changeNullValueToEmpty(userDetailRequest.value.UseEndDate),
                CmpContPks: Utils.isNullEmptyArray(userDetailRequest.value.CmpContPks) ? null : userDetailRequest.value.CmpContPks,
                Birthday: Utils.changeNullValueToEmpty(userDetailRequest.value.Birthday),
                Gender: Utils.changeNullValueToEmpty(userDetailRequest.value.Gender),
                Language: Utils.changeNullValueToEmpty(userDetailRequest.value.Language),
                JoinedDate: Utils.changeNullValueToEmpty(userDetailRequest.value.JoinedDate),
                LeavedDate: Utils.changeNullValueToEmpty(userDetailRequest.value.LeavedDate),
                IncumbentStatus: Utils.changeNullValueToEmpty(userDetailRequest.value.IncumbentStatus),
                OrganizationPk: Utils.changeNullValueToEmpty(userDetailRequest.value.OrganizationPk),
                JobCategoryPk: Utils.changeNullValueToEmpty(userDetailRequest.value.JobCategoryPk),
                PositionPk: Utils.changeNullValueToEmpty(userDetailRequest.value.PositionPk),
                EmploymentTypePk: Utils.changeNullValueToEmpty(userDetailRequest.value.EmploymentTypePk),
                LocationPk: Utils.changeNullValueToEmpty(userDetailRequest.value.LocationPk),
                OvertimePk: Utils.changeNullValueToEmpty(userDetailRequest.value.OvertimePk),
            };
            const response = await managementListStore.postCliDtlAction(API_URL, requestBody);
            if (response.success) {
                closeAndUpdate();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            if (userDetailRequest.value.UserPk === '') {
                // 新規登録の場合。
                const requestBody = {
                    UserId: userDetailRequest.value.UserId,
                    EmployeeNo: userDetailRequest.value.EmployeeNo,
                    MailAddress: userDetailRequest.value.MailAddress,
                    IniPwdType: userDetailRequest.value.IniPwdType,
                    IniPwdFree: userDetailRequest.value.IniPwdFree,
                    NameKjLs: userDetailRequest.value.NameKjLs,
                    NameKjFs: userDetailRequest.value.NameKjFs,
                    NameKnLs: Utils.changeNullValueToEmpty(userDetailRequest.value.NameKnLs),
                    NameKnFs: Utils.changeNullValueToEmpty(userDetailRequest.value.NameKnFs),
                    SuspendFlag: userDetailRequest.value.SuspendFlag,
                    UseStartDate: Utils.changeNullValueToEmpty(userDetailRequest.value.UseStartDate),
                    UseEndDate: Utils.changeNullValueToEmpty(userDetailRequest.value.UseEndDate),
                    CmpContPks: Utils.isNullEmptyArray(userDetailRequest.value.CmpContPks) ? null : userDetailRequest.value.CmpContPks,
                    Birthday: Utils.changeNullValueToEmpty(userDetailRequest.value.Birthday),
                    Gender: Utils.changeNullValueToEmpty(userDetailRequest.value.Gender),
                    Language: Utils.changeNullValueToEmpty(userDetailRequest.value.Language),
                    JoinedDate: Utils.changeNullValueToEmpty(userDetailRequest.value.JoinedDate),
                    LeavedDate: Utils.changeNullValueToEmpty(userDetailRequest.value.LeavedDate),
                    IncumbentStatus: Utils.changeNullValueToEmpty(userDetailRequest.value.IncumbentStatus),
                    OrganizationPk: Utils.changeNullValueToEmpty(userDetailRequest.value.OrganizationPk),
                    JobCategoryPk: Utils.changeNullValueToEmpty(userDetailRequest.value.JobCategoryPk),
                    PositionPk: Utils.changeNullValueToEmpty(userDetailRequest.value.PositionPk),
                    EmploymentTypePk: Utils.changeNullValueToEmpty(userDetailRequest.value.EmploymentTypePk),
                    LocationPk: Utils.changeNullValueToEmpty(userDetailRequest.value.LocationPk),
                    OvertimePk: Utils.changeNullValueToEmpty(userDetailRequest.value.OvertimePk),
                };
                const response = await managementListStore.postCliDtlAction(API_URL, requestBody);
                if (response.success) {
                    closeAndUpdate();
                } else {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return;
                }
            } else {
                // 編集モードの場合。
                const requestBody = {
                    UserPk: userDetailRequest.value.UserPk, // 更新対象の利用者のPK情報をそのまま保持する。
                    EmployeeNo: userDetailRequest.value.EmployeeNo,
                    MailAddress: userDetailRequest.value.MailAddress,
                    IniPwdType: userDetailRequest.value.IniPwdType,
                    IniPwdFree: userDetailRequest.value.IniPwdFree,
                    NameKjLs: userDetailRequest.value.NameKjLs,
                    NameKjFs: userDetailRequest.value.NameKjFs,
                    NameKnLs: Utils.changeNullValueToEmpty(userDetailRequest.value.NameKnLs),
                    NameKnFs: Utils.changeNullValueToEmpty(userDetailRequest.value.NameKnFs),
                    SuspendFlag: userDetailRequest.value.SuspendFlag,
                    UseStartDate: Utils.changeNullValueToEmpty(userDetailRequest.value.UseStartDate),
                    UseEndDate: Utils.changeNullValueToEmpty(userDetailRequest.value.UseEndDate),
                    CmpContPks: Utils.isNullEmptyArray(userDetailRequest.value.CmpContPks) ? null : userDetailRequest.value.CmpContPks,
                    Birthday: Utils.changeNullValueToEmpty(userDetailRequest.value.Birthday),
                    Gender: Utils.changeNullValueToEmpty(userDetailRequest.value.Gender),
                    Language: Utils.changeNullValueToEmpty(userDetailRequest.value.Language),
                    JoinedDate: Utils.changeNullValueToEmpty(userDetailRequest.value.JoinedDate),
                    LeavedDate: Utils.changeNullValueToEmpty(userDetailRequest.value.LeavedDate),
                    IncumbentStatus: Utils.changeNullValueToEmpty(userDetailRequest.value.IncumbentStatus),
                    OrganizationPk: Utils.changeNullValueToEmpty(userDetailRequest.value.OrganizationPk),
                    JobCategoryPk: Utils.changeNullValueToEmpty(userDetailRequest.value.JobCategoryPk),
                    PositionPk: Utils.changeNullValueToEmpty(userDetailRequest.value.PositionPk),
                    EmploymentTypePk: Utils.changeNullValueToEmpty(userDetailRequest.value.EmploymentTypePk),
                    LocationPk: Utils.changeNullValueToEmpty(userDetailRequest.value.LocationPk),
                    OvertimePk: Utils.changeNullValueToEmpty(userDetailRequest.value.OvertimePk),
                };
                const response = await managementListStore.putCliDtlAction(API_URL, requestBody);
                if (response.success) {
                    closeAndUpdate();
                } else {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return;
                }
            }
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 自己サインアップユーザーは、ユーザーIDなどの判定が不要なので、現在のユーザーの登録状態で判定する。
        if (userDetailRequest.value.RegistMethod !== '2' && userDetailRequest.value.RegistMethod !== '4') {
            // ユーザーID
            if (Utils.isNullEmpty(userDetailRequest.value.UserId)) {
                message += i18n.global.t('ManagementDetail.message.message1') + '\n';
            } else {
                // 文字数
                if (Utils.checkLengthOfUserId(userDetailRequest.value.UserId)) {
                    message += i18n.global.t('ManagementDetail.message.message13') + '\n';
                }
                // 形式
                if (!Utils.isMatchPatternLowerAlphabetNumber(userDetailRequest.value.UserId)) {
                    message += i18n.global.t('ManagementDetail.message.message14') + '\n';
                }
            }
            // 従業員番号
            if (Utils.isNullEmpty(userDetailRequest.value.EmployeeNo)) {
                message += i18n.global.t('ManagementDetail.message.message2') + '\n';
            } else {
                // 文字数
                if (Utils.checkLengthOfString(userDetailRequest.value.EmployeeNo)) {
                    message += i18n.global.t('ManagementDetail.message.message15') + '\n';
                }
                // 形式
                if (!Utils.isMatchPatternLowerAlphabetNumber(userDetailRequest.value.EmployeeNo)) {
                    message += i18n.global.t('ManagementDetail.message.message21') + '\n';
                }
            }
            // 氏名(漢字)(性)
            if (Utils.isNullEmpty(userDetailRequest.value.NameKjLs)) {
                message += i18n.global.t('ManagementDetail.message.message3') + '\n';
            } else {
                // 文字数
                if (Utils.checkLengthOfShortString(userDetailRequest.value.NameKjLs)) {
                    message += i18n.global.t('ManagementDetail.message.message16') + '\n';
                }
            }
            // 氏名(漢字)(名)
            if (Utils.isNullEmpty(userDetailRequest.value.NameKjFs)) {
                message += i18n.global.t('ManagementDetail.message.message4') + '\n';
            } else {
                // 文字数
                if (Utils.checkLengthOfShortString(userDetailRequest.value.NameKjFs)) {
                    message += i18n.global.t('ManagementDetail.message.message17') + '\n';
                }
            }
            // 氏名(カナ)(性)
            if (!Utils.isNullEmpty(userDetailRequest.value.NameKnLs)) {
                // 文字数
                if (Utils.checkLengthOfString(userDetailRequest.value.NameKnLs)) {
                    message += i18n.global.t('ManagementDetail.message.message18') + '\n';
                }
                // 形式
                if (!Utils.isMatchPatternKana(userDetailRequest.value.NameKnLs)) {
                    message += i18n.global.t('ManagementDetail.message.message7') + '\n';
                }
            }
            // 氏名(カナ)(名)
            if (!Utils.isNullEmpty(userDetailRequest.value.NameKnFs)) {
                // 文字数
                if (Utils.checkLengthOfString(userDetailRequest.value.NameKnFs)) {
                    message += i18n.global.t('ManagementDetail.message.message19') + '\n';
                }
                // 形式
                if (!Utils.isMatchPatternKana(userDetailRequest.value.NameKnFs)) {
                    message += i18n.global.t('ManagementDetail.message.message8') + '\n';
                }
            }
            // 初期パスワードの設定モードが「設定(変更)しない」以外の場合は、メールアドレスか任意文字列のパスワードの入力のチェックを行う。
            if (userDetailRequest.value.IniPwdType !== '0') {
                // メールアドレス
                if (!Utils.isNullEmpty(userDetailRequest.value.MailAddress)) {
                    // 文字数
                    if (Utils.checkLengthOfMailAddress(userDetailRequest.value.MailAddress)) {
                        message += i18n.global.t('ManagementDetail.message.message11') + '\n';
                    }
                    // 形式
                    if (!Utils.isMatchPatternMailAddress(userDetailRequest.value.MailAddress)) {
                        message += i18n.global.t('ManagementDetail.message.message12') + '\n';
                    }
                } else {
                    // パスワード
                    if (!Utils.isNullEmpty(userDetailRequest.value.IniPwdFree)) {
                        // 文字数
                        if (Utils.checkLengthOfPassword(userDetailRequest.value.IniPwdFree)) {
                            message += i18n.global.t('ManagementDetail.message.message9') + '\n';
                        }
                        // 形式
                        if (!Utils.isMatchPatternAlphabetNumberSymbol(userDetailRequest.value.IniPwdFree)) {
                            message += i18n.global.t('ManagementDetail.message.message10') + '\n';
                        }
                    } else {
                        // 初期パスワードを設定するモードにも関わらず、メールアドレスと任意文字列パスワードが未入力の場合は、メールアドレスの入力を必須にする。
                        message += i18n.global.t('ManagementDetail.message.message20') + '\n';
                    }
                }
            }
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * 組織セレクトボックス取得処理
     */
    async function getOrganizationIssue() {
        // Storeを初期化する。
        managementListStore.organizationIssue = [];
        managementListStore.organizationInfo = {};
        organizationIssue.splice(0);

        // 現在の親組織IDの座標を初期化する。
        // 親組織のプルダウンの内容に応じて、座標を制御する。
        let parentLevel = 0;

        // 親組織プルダウンが変更されて、現在の親組織のPK情報が取得できたか判定する。
        if (!Utils.isNullEmpty(userDetailRequest.value.OrganizationPk)) {
            // 親組織の組織名を取得するために、親組織PKをキーに、企業組織取得APIをコールする。
            //await getOrganizationInfo(organizationInfoRequest.value.OrganizationPk);

            const queryParam = {
                OrganizationPk: userDetailRequest.value.OrganizationPk,
            };
            const response = await managementListStore.getOrgDtlForParenAction(API_URL, queryParam);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }

            // 取得した親組織の階層レベルを格納する。
            parentLevel = managementListStore.organizationParentInfo.OrganizationLv;
        }

        // 現在の座標が取得できなかった場合。つまり、第1階層まで戻ってきた場合。
        if (parentLevel === 0) {
            // 第1階層のルート。

            // 初期値は選択してくださいを設定する。
            const defalutOption = {
                pk: '',
                name: i18n.global.t('ManagementDetail.parts.dl38'),
            };
            organizationIssue.push(defalutOption);

            // 選択してくださいを初期値に設定する。
            userDetailRequest.value.OrganizationPk = '';

            // 第1階層の組織データをプルダウンに生成するためのリクエストボディ条件を制御する。
            const requestBody = {};

            // 企業組織選択APIをコールする。
            const response = await managementListStore.getOrgSelAction(API_URL, requestBody);

            if (response.success) {
                if (!Utils.isNullEmptyArray(managementListStore.organizationIssue)) {
                    for (const org of managementListStore.organizationIssue) {
                        // 第1階層に属する組織の選択肢を生成する。
                        const option = {
                            pk: org.pk,
                            name: org.name,
                        };
                        organizationIssue.push(option);
                    }
                }
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            // 第1階層以外の場合。

            // 上の階層に戻るの選択肢。
            // 自分の親の親階層に戻る。
            const backToParentOrgOption = {
                pk: Utils.changeNullValueToEmpty(managementListStore.organizationParentInfo.ParentOrganizationPk),
                name: i18n.global.t('ManagementDetail.parts.dl40'),
            };
            organizationIssue.push(backToParentOrgOption);

            // 自分の親階層の情報を設定する。
            const defalutOption = {
                pk: userDetailRequest.value.OrganizationPk,
                name: managementListStore.organizationParentInfo.OrganizationNmJ,
            };
            organizationIssue.push(defalutOption);

            // 企業組織選択APIのクエリパラメーターを生成する。
            // 座標は、自分自身の組織PKを設定する。
            const queryParam = {
                OrganizationPk: userDetailRequest.value.OrganizationPk,
            };

            // 企業組織選択APIをコールする。
            const response2 = await managementListStore.getOrgSelAction(API_URL, queryParam);
            if (response2.success) {
                if (!Utils.isNullEmptyArray(managementListStore.organizationIssue)) {
                    // 更新モードかどうかを判定する。
                    if (!Utils.isNullEmpty(userDetailRequest.value.OrganizationPk)) {
                        // 更新モードの場合は、自分自身の組織の選択肢を親組織のプルダウンの一覧から除外する。
                        // 自分自身を除外したことで、自分の配下の組織も選択できないので、結果的に表示されない。
                        for (const org of managementListStore.organizationIssue) {
                            if (org.pk !== userDetailRequest.value.OrganizationPk) {
                                const option = {
                                    pk: org.pk,
                                    name: '　' + org.name,
                                };
                                organizationIssue.push(option);
                            }
                        }
                    } else {
                        // 自分自身の組織PKで無ければ、全部の自階層と同一の組織をプルダウン選択肢に生成する。
                        for (const org of managementListStore.organizationIssue) {
                            const option = {
                                pk: org.pk,
                                name: '　' + org.name,
                            };
                            organizationIssue.push(option);
                        }
                    }
                }
            } else {
                messageDialog('error', response2.errorMessage);
                if (response2.redirect) {
                    location.href = response2.redirectUrl;
                }
                return;
            }
        }
    }

    /**
     * 企業契約情報取得処理
     */
    async function getCintractList() {
        const queryParam = {};
        const response = await managementListStore.getCmpConAction(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }
    /**
     * ユーザー選択モーダルから管理者詳細モーダルを開くイベントが実行された場合の処理。
     * @param {String} userPk
     * @returns なし
     */
    async function selectModal(UserPk) {
        // 初期化する。
        await reset();

        // ユーザー引き継ぎ時の初期値を設定する。
        userSelectFlg.value = true;

        // 利用者詳細取得APIをコールする。
        await getInfoFunction(UserPk);
    }

    /**
     * 親画面で実装必須関数
     */
    const emit = defineEmits(['detailModal:cancel', 'detailModal:closeAndUpdate']);
    function cancel() {
        // 親画面のキャンセル処理を呼び出す
        emit('detailModal:cancel');
    }
    function closeAndUpdate() {
        // 親画面の、モーダルを閉じて、一覧画面の再描画を行う処理を呼び出す。
        emit('detailModal:closeAndUpdate');
    }
</script>
<template>
    <article id="modalUserListUserDetail" class="modal" :style="{ display: 'block' }">
        <h3 class="textCenter">{{ $t('ManagementDetail.parts.pageTitle1') }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <section class="form dtWidth170">
                <!-- 基本情報 -->
                <h4>{{ $t('ManagementDetail.parts.pageTitle2') }}</h4>
                <dl class="noBorder" style="margin-bottom: 15px">
                    <span>{{ $t('ManagementDetail.parts.pageInfo1') }} </span>
                </dl>
                <dl>
                    <!--ユーザーID-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl2') }} </label>
                            </li>
                            <li class="must">
                                <span>{{ $t('ManagementDetail.parts.dl1') }} </span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" v-model="userDetailRequest.UserId" :disabled="disabledUserPk" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--従業員番号-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl3') }} </label>
                            </li>
                            <li class="must">
                                <span>{{ $t('ManagementDetail.parts.dl1') }} </span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" v-model="userDetailRequest.EmployeeNo" :disabled="disabledBasicInfo" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--メールアドレス-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl4') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" v-model="userDetailRequest.MailAddress" :disabled="disabledBasicInfo" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--初期パスワード-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl5') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul>
                            <li>
                                <!--設定する-->
                                <input type="radio" class="radio" id="radio-1" name="radio-group-1" value="1" v-model="pwdType" :disabled="disabledIniPwdType1" />
                                <label class="radio label" style="width: 325px" for="radio-1">{{ $t('ManagementDetail.parts.pageInfo2') }} </label>
                                <div style="padding-left: 28px">
                                    <p>
                                        {{ $t('ManagementDetail.parts.pageInfo3') }}<br />{{ $t('ManagementDetail.parts.pageInfo4') }} <br />
                                        <br />
                                        {{ $t('ManagementDetail.parts.pageInfo5') }}<br />
                                        {{ $t('ManagementDetail.parts.pageInfo6') }}
                                    </p>
                                    <ul style="display: flex; align-items: center; margin-top: 5px; position: relative">
                                        <!--任意文字列のパスワード-->
                                        <li style="margin-right: 40px; display: flex; align-items: center">
                                            <span>{{ $t('ManagementDetail.parts.dl6') }} </span>
                                            <Tooltip :buttonTop="0" :buttonLeft="0" :divBottom="0" :divLeft="0" :divWidth="230">{{ $t('ManagementDetail.parts.blowoutInformation1') }}</Tooltip>
                                        </li>
                                        <li>
                                            <div class="passwordBlock">
                                                <PasswordInput ref="passwordInputComponent" v-model:input="userDetailRequest.IniPwdFree" :disabledMode="disabledIniPwdFree" />
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </li>
                            <li style="margin-top: 5px; margin-bottom: 10px">
                                <!--設定しない-->
                                <input type="radio" class="radio" id="radio-2" name="radio-group-1" value="0" v-model="pwdType" :disabled="disabledIniPwdType2" />
                                <label class="radio label" style="width: 134px" for="radio-2">{{ $t('ManagementDetail.parts.dl7') }} </label>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--氏名（漢字）-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl8') }} </label>
                            </li>
                            <li class="must">
                                <span>{{ $t('ManagementDetail.parts.dl1') }} </span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" class="smallField" v-model="userDetailRequest.NameKjLs" :disabled="disabledBasicInfo" />
                            </li>
                            <li>
                                <input type="text" class="smallField" v-model="userDetailRequest.NameKjFs" :disabled="disabledBasicInfo" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--氏名（カナ）-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl9') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" class="smallField" v-model="userDetailRequest.NameKnLs" :disabled="disabledBasicInfo" />
                            </li>
                            <li>
                                <input type="text" class="smallField" v-model="userDetailRequest.NameKnFs" :disabled="disabledBasicInfo" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--管理者の登録-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl10') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span v-if="userDetailRequest.UserPk === ''">{{ $t('ManagementDetail.parts.dl12') }} </span>
                                <span v-else-if="userDetailRequest.MngFlag">{{ $t('ManagementDetail.parts.dl11') }} </span>
                                <span v-else>{{ $t('ManagementDetail.parts.dl12') }} </span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--登録契機-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl13') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span v-if="userDetailRequest.RegistMethod === '1' || userDetailRequest.UserPk === ''">{{ $t('ManagementDetail.parts.dl14') }} </span>
                                <span v-else-if="userDetailRequest.RegistMethod === '2'">{{ $t('ManagementDetail.parts.dl15') }} </span>
                                <span v-else-if="userDetailRequest.RegistMethod === '3'">{{ $t('ManagementDetail.parts.dl16') }} </span>
                                <span v-else-if="userDetailRequest.RegistMethod === '4'">{{ $t('ManagementDetail.parts.dl17') }} </span>
                                <span v-else-if="userDetailRequest.RegistMethod === '5'">{{ $t('ManagementDetail.parts.dl18') }} </span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--認証方法-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl19') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span v-if="userDetailRequest.AuthenticateMethod === '1' || userDetailRequest.UserPk === ''">{{ $t('ManagementDetail.parts.dl20') }} </span>
                                <span v-else-if="userDetailRequest.AuthenticateMethod === '2'">{{ $t('ManagementDetail.parts.dl21') }} </span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl style="display: none">
                    <!--最終ログイン時刻-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl22') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ userDetailRequest.LastLoginDt }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--利用停止-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl23') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li style="width: 21px">
                                <input type="checkbox" class="checkbox" id="checkbox-1" v-model="userDetailRequest.SuspendFlag" :value="true" />
                                <label class="checkbox" for="checkbox-1" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl class="noBorder" style="margin-bottom: unset">
                    <!--利用開始日、利用終了日-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl24') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li style="margin-right: 5px; width: 210px">
                                <label class="calendar" style="width: 210px">
                                    <input type="date" :class="[selectedDate1 !== '' ? 'fillterText' : 'fillterText blank']" placeholder="日付を選択してください" v-model="selectedDate1" :disabled="disabledUseStartEndDate" />
                                </label>
                            </li>
                            <li style="width: auto; margin-right: 5px">
                                <span>-</span>
                            </li>
                            <li>
                                <label class="calendar" style="width: 210px">
                                    <input type="date" :class="[selectedDate2 !== '' ? 'fillterText' : 'fillterText blank']" placeholder="日付を選択してください" v-model="selectedDate2" :disabled="disabledUseStartEndDate" />
                                </label>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl style="padding-bottom: 10px">
                    <dt />
                    <dd>
                        <p>{{ $t('ManagementDetail.parts.pageInfo7') }}</p>
                        <p>{{ $t('ManagementDetail.parts.pageInfo8') }}</p>
                        <p style="padding-left: 18px">{{ $t('ManagementDetail.parts.pageInfo9') }}</p>
                    </dd>
                </dl>
                <!-- 契約割り当て -->
                <h4 style="margin-top: 40px">{{ $t('ManagementDetail.parts.pageTitle3') }}</h4>
                <dl class="noBorder">
                    <!--契約-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl25') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <section class="innerTable" style="height: auto">
                            <table class="table">
                                <tbody>
                                    <tr v-for="(data, index) in managementListStore.contractList" :key="index">
                                        <td style="width: 20px">
                                            <input type="checkbox" :id="'data6-' + (index + 1)" class="checkbox" v-model="userDetailRequest.CmpContPks" :value="data.CmpContPk" :disabled="disabledContract || data.TouDataFlag" />
                                            <label class="checkbox" :for="'data6-' + (index + 1)" />
                                        </td>
                                        <td style="width: auto">
                                            <span>{{ index + 1 }}. {{ data.CmpContNm }} ({{ data.CmpContPeriodS }} - {{ data.CmpContPeriodE }})</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </section>
                    </dd>
                </dl>
                <!-- 付加情報 -->
                <h4 style="margin-top: 40px">
                    {{ $t('ManagementDetail.parts.pageTitle4') }} <span style="font-size: 12px; font-weight: 400"> {{ $t('ManagementDetail.parts.pageInfo10') }} </span>
                </h4>
                <dl>
                    <!--生年月日-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl26') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <label class="calendar">
                                    <input type="date" :class="[selectedDate3 !== '' ? 'fillterText' : 'fillterText blank']" placeholder="日付を選択してください" v-model="selectedDate3" :disabled="disabledAdditionalInfo" />
                                </label>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--性別-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl27') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <select v-model="userDetailRequest.Gender" :disabled="disabledAdditionalInfo" required>
                                        <option value="" disabled>{{ $t('ManagementDetail.parts.dl38') }}</option>
                                        <option v-for="gender in genderInfo" :key="gender.pk" :value="gender.value">
                                            {{ gender.name }}
                                        </option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--使用言語-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl28') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <select v-model="userDetailRequest.Language" :disabled="disabledAdditionalInfo" required>
                                        <option value="" disabled>{{ $t('ManagementDetail.parts.dl38') }}</option>
                                        <option v-for="language in languageInfo" :key="language.pk" :value="language.value">
                                            {{ language.name }}
                                        </option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--入社年月日-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl29') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <label class="calendar">
                                    <input type="date" :class="[selectedDate4 !== '' ? 'fillterText' : 'fillterText blank']" placeholder="日付を選択してください" v-model="selectedDate4" :disabled="disabledAdditionalInfo" />
                                </label>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--退職年月日-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl30') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <label class="calendar">
                                    <input type="date" :class="[selectedDate5 !== '' ? 'fillterText' : 'fillterText blank']" placeholder="日付を選択してください" v-model="selectedDate5" :disabled="disabledAdditionalInfo" />
                                </label>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--在籍ステータス-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl31') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <select v-model="userDetailRequest.IncumbentStatus" :disabled="disabledAdditionalInfo" required>
                                        <option value="" disabled>{{ $t('ManagementDetail.parts.dl38') }}</option>
                                        <option v-for="incumbentStatus in incumbentStatusInfo" :key="incumbentStatus.pk" :value="incumbentStatus.value">
                                            {{ incumbentStatus.name }}
                                        </option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--組織-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl32') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <select v-model="userDetailRequest.OrganizationPk" @change="getOrganizationIssue" :disabled="disabledAdditionalInfo" required>
                                        <option v-for="organization in organizationIssue" :key="organization.pk" :value="organization.pk">
                                            {{ organization.name }}
                                        </option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--職種-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl33') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <select v-model="userDetailRequest.JobCategoryPk" :disabled="disabledAdditionalInfo" required>
                                        <option value="">{{ $t('ManagementDetail.parts.dl38') }}</option>
                                        <option v-for="contents in managementListStore.jobCategoryIssue" :key="contents.pk" :value="contents.pk">
                                            {{ contents.name }}
                                        </option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--職位-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl34') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <select v-model="userDetailRequest.PositionPk" :disabled="disabledAdditionalInfo" required>
                                        <option value="">{{ $t('ManagementDetail.parts.dl38') }}</option>
                                        <option v-for="contents in managementListStore.positionIssue" :key="contents.pk" :value="contents.pk">
                                            {{ contents.name }}
                                        </option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--雇用形態-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl35') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <select v-model="userDetailRequest.EmploymentTypePk" :disabled="disabledAdditionalInfo" required>
                                        <option value="">{{ $t('ManagementDetail.parts.dl38') }}</option>
                                        <option v-for="contents in managementListStore.employmentTypeIssue" :key="contents.pk" :value="contents.pk">
                                            {{ contents.name }}
                                        </option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--勤務地-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl36') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <select v-model="userDetailRequest.LocationPk" :disabled="disabledAdditionalInfo" required>
                                        <option value="">{{ $t('ManagementDetail.parts.dl38') }}</option>
                                        <option v-for="contents in managementListStore.locationIssue" :key="contents.pk" :value="contents.pk">
                                            {{ contents.name }}
                                        </option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl class="marginBottom15">
                    <!--時間外労働時間-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementDetail.parts.dl37') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <select v-model="userDetailRequest.OvertimePk" :disabled="disabledAdditionalInfo" required>
                                        <option value="">{{ $t('ManagementDetail.parts.dl38') }}</option>
                                        <option v-for="contents in managementListStore.overTimeIssue" :key="contents.pk" :value="contents.pk">
                                            {{ contents.name }}
                                        </option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <template v-if="userKeyNoSupport">
                    <!-- ユーザーキー番号 -->
                    <h4 style="margin-top: 40px">{{ $t('ManagementDetail.parts.pageTitle5') }}</h4>
                    <dl class="marginBottom15">
                        <!--ユーザーキー番号名-->
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ Utils.changeNullValueToEmpty(userKeyNoNm) }}</label>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li>
                                    <span>{{ Utils.changeNullValueToEmpty(userDetailRequest.UserKeyNo) }}</span>
                                </li>
                            </ul>
                        </dd>
                    </dl>
                </template>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal closeModal" @click="cancel()">{{ $t('ManagementDetail.parts.button1') }}</button>
                </li>
                <li>
                    <button type="button" class="btnPrimary closeModal" @click="createDetail()" :disabled="disabledStore.isDisabled">{{ $t('ManagementDetail.parts.button2') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>
<style></style>
